import { useEffect } from 'react';
import {
  Button,
  ButtonProps,
  Col,
  Form,
  FormProps,
  Row,
} from 'antd';
import { Gutter } from 'antd/lib/grid/row';

import { MilesText } from 'components';

import { TFieldProps } from 'types';

import { FieldByType } from './FieldByType';

import './MilesForm.styles.scss';

const { Item } = Form;

export interface IMilesFormProps<T> extends Omit<FormProps<T>, 'fields'> {
  fields: TFieldProps[];
  title?: string;
  gutter?: Gutter | [Gutter, Gutter];
  actions?: React.ReactNode;
  buttonProps?: ButtonProps & { label: string; };
}

export const MilesForm = <T, >({
  form,
  name,
  colon,
  title,
  gutter,
  layout,
  fields,
  actions,
  labelCol,
  disabled,
  wrapperCol,
  buttonProps,
  initialValues,
  onChange,
  onFinish,
  onFieldsChange,
}: IMilesFormProps<T>) => {
  useEffect(() => {
    if (!buttonProps?.loading) {
      form?.resetFields();
    }
  }, [initialValues]);

  return (
    <>
      {title && (
        <Row className='mb-6'>
          <MilesText
            size='lg'
            className='font-semibold'
          >
            {title}
          </MilesText>
        </Row>
      )}

      <Form
        labelWrap
        name={name}
        form={form}
        colon={colon}
        layout={layout}
        disabled={disabled}
        labelCol={labelCol || { span: 8 }}
        className='miles-form'
        wrapperCol={wrapperCol || { span: 16 }}
        initialValues={initialValues}
        onChange={onChange}
        onFinish={onFinish}
        onFieldsChange={onFieldsChange}
      >
        <Row gutter={gutter}>
          {fields.map((field) => (
            <Col
              key={Array.isArray(field.name) ? field.name.join(',') : field.name}
              xs={field.colProps?.xs || 24}
              sm={field.colProps?.sm}
              md={field.colProps?.md}
              lg={field.colProps?.lg}
              xl={field.colProps?.xl}
              xxl={field.colProps?.xxl}
            >
              <FieldByType field={field} />
            </Col>
          ))}

          {buttonProps && (
            <Col flex={1}>
              <Row justify='end'>
                <Item
                  shouldUpdate
                  wrapperCol={{ offset: labelCol?.span, span: wrapperCol?.span }}
                >
                  {() => (
                    <Button
                      htmlType='submit'
                      ghost={buttonProps.ghost}
                      danger={buttonProps.danger}
                      type={buttonProps.type || 'primary'}
                      loading={buttonProps.loading}
                      onClick={buttonProps.onClick}
                      className={buttonProps.className}
                      disabled={
                        buttonProps.disabled
                          || !form?.isFieldsTouched()
                          || form.getFieldsError().some(({ errors }) => !!errors.length)
                      }
                    >
                      {buttonProps.label}
                    </Button>
                  )}
                </Item>
              </Row>
            </Col>
          )}
        </Row>

        {actions}
      </Form>
    </>
  );
};
